import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { BaseComponent } from '../../../base/base.component';
import { SelectFieldComponent } from '../../../core/fields/select-field.component';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-state-criteria',
  imports: [CommonModule, SelectFieldComponent],
  template: `
    <div class="tw-pt-2">
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        <ng-template #select let-control="control" let-label="label">
          <app-select-field
            labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
            valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
            [control]="control"
            optionLabel="description"
            optionValue="value"
            [multiple]="true"
            [multipleFilter]="true"
            [chip]="true"
            [options]="states"
            [showHelpText]="false"
            [loading]="loading"
            [label]="label"
          />
        </ng-template>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              label: 'Jurisdiction state',
              control: fg.controls.jurisdictionStates,
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: { label: 'Loss state', control: fg.controls.lossStates }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              label: 'Payroll state',
              control: fg.controls.payrollStates,
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              label: 'Premium state',
              control: fg.controls.premiumStates,
            }
          "
        ></ng-container>
      </div>
    </div>
  `,
  styles: `
    :host ::ng-deep app-select-field {
      display: block;
      min-width: 0;
    }
    :host ::ng-deep .p-select-label > .option-label,
    :host ::ng-deep .p-multiselect-label .option-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
})
export class StateCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  states: ListItem[] = [];
  statesCount = 0;
  loading = true;

  @Input({ required: true })
  section: FilterSection;

  fg = new FormGroup({
    jurisdictionStates: new FormControl<string[]>([]),
    lossStates: new FormControl<string[]>([]),
    payrollStates: new FormControl<string[]>([]),
    premiumStates: new FormControl<string[]>([]),
  });

  setStatesFromCriteria(criteria: FilterCriteria[]) {
    const jurisdictionStates = criteria
      .filter((c) => c.column === 'STATE_OF_JURISDICTION')
      .flatMap((c) => c.values);

    const lossStates = criteria
      .filter((c) => c.column === 'STATE_OF_LOSS')
      .flatMap((c) => c.values);

    const payrollStates = criteria
      .filter((c) => c.column === 'STATE_OF_PAYROLL_OR_PREMIUM')
      .flatMap((c) => c.values);

    const premiumStates = criteria
      .filter((c) => c.column === 'PREMIUM_STATE')
      .flatMap((c) => c.values);

    if (jurisdictionStates.length) {
      this.fg.controls.jurisdictionStates.setValue(jurisdictionStates);
    }
    if (lossStates.length) {
      this.fg.controls.lossStates.setValue(lossStates);
    }
    if (payrollStates.length) {
      this.fg.controls.payrollStates.setValue(payrollStates);
    }
    if (premiumStates.length) {
      this.fg.controls.premiumStates.setValue(premiumStates);
    }
  }

  @HandleLoadingState()
  async ngOnInit() {
    this.section.criteria = this;
    this.states = await this.api.filtersCriteria.getStates();

    this.dispose.subs =
      this.fg.controls.jurisdictionStates.valueChanges.subscribe(() =>
        this.emitCount(),
      );
    this.dispose.subs = this.fg.controls.lossStates.valueChanges.subscribe(() =>
      this.emitCount(),
    );
    this.dispose.subs = this.fg.controls.payrollStates.valueChanges.subscribe(
      () => this.emitCount(),
    );
    this.dispose.subs = this.fg.controls.premiumStates.valueChanges.subscribe(
      () => this.emitCount(),
    );

    if (this.section.values?.length) {
      this.setStatesFromCriteria(this.section.values);
    }
  }

  private emitCount() {
    const totalCount =
      (this.fg.controls.jurisdictionStates.value?.length || 0) +
      (this.fg.controls.lossStates.value?.length || 0) +
      (this.fg.controls.payrollStates.value?.length || 0) +
      (this.fg.controls.premiumStates.value?.length || 0);
    this.section.count.set(totalCount);
  }

  valid(): boolean {
    return true;
  }

  getCriteria(): FilterCriteria[] {
    const jurisdictionCriteria = (
      this.fg.controls.jurisdictionStates.value || []
    ).map(
      (value) =>
        ({
          tableAlias: 'claim',
          column: 'STATE_OF_JURISDICTION',
          displayName: '',
          operator: 'EQUALS',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'State',
          filterSubsection: '',
        } as FilterCriteria),
    );

    const lossCriteria = (this.fg.controls.lossStates.value || []).map(
      (value) =>
        ({
          tableAlias: 'claim',
          column: 'STATE_OF_LOSS',
          displayName: '',
          operator: 'EQUALS',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'State',
          filterSubsection: '',
        } as FilterCriteria),
    );

    const payrollCriteria = (this.fg.controls.payrollStates.value || []).map(
      (value) =>
        ({
          tableAlias: 'claim',
          column: 'STATE_OF_PAYROLL_OR_PREMIUM',
          displayName: '',
          operator: 'EQUALS',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'State',
          filterSubsection: '',
        } as FilterCriteria),
    );

    const premiumCriteria = (this.fg.controls.premiumStates.value || []).map(
      (value) =>
        ({
          tableAlias: 'claim_policy',
          column: 'PREMIUM_STATE',
          displayName: '',
          operator: 'EQUALS',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'State',
          filterSubsection: '',
        } as FilterCriteria),
    );

    return [
      ...jurisdictionCriteria,
      ...lossCriteria,
      ...payrollCriteria,
      ...premiumCriteria,
    ];
  }
}
